/**
 * F-174 phase 1: edit locks on the platform socket (no new socket).
 *
 * A piece of an item (a campaign/e-mail block per language, an event section,
 * or `item` for a post, article or episode) is locked by the editor that has
 * it focused, inside the item's room (`item:<type>:<id>`). The server only
 * accepts a lock from a socket that joined the room as an editor
 * (`presence.join { room, editing: true }`, answered with `presence.joined`),
 * so an acquire waits here until that answer (or the room's `locks` snapshot)
 * came after the last `ready`.
 *
 *   client → `lock.acquire { room, block, language?, takeover? }`, `lock.release { room, block, language? }`
 *   server → `lock.acquired { …, expiresAt, graceUntil }`, `lock.denied { …, reason, status, holder?, canTakeOver? }`,
 *            `lock.released`, `lock.lost { …, reason, by }` (only to the loser),
 *            `locks { room, locks: [{ block, language, mine, holder, expiresAt }] }` to every editor of the room.
 *
 * A lock lives 30 s after the holder's last signal; the activity the presence
 * store already sends (`presence.active`) keeps it, so nothing here renews it.
 * Frames go one at a time, spaced, so they never add up to the server's limit
 * of 5 frames a second with the presence and room frames.
 *
 * Pure TypeScript (no React, no `@/` imports): tested by `tests/editLocks.test.ts`.
 */

export interface LockHolder {
  memberId: string
  name: string | null
  avatarUrl: string | null
}

/** One live lock of a room, as the server shows it to this socket. `holder` null: hidden (chat off or a chat block). */
export interface RoomLock {
  block: string
  language: string | null
  /** Held by this socket (another tab of the same person is not `mine`). */
  mine: boolean
  holder: LockHolder | null
  expiresAt: string | null
}

export type LockDeniedReason = 'held' | 'forbidden' | 'notEditing' | 'invalid' | 'tooMany'
export type LockLostReason = 'takeover' | 'idle' | 'moved' | 'access'

export interface LockKey {
  room: string
  block: string
  language: string | null
}

export type LockEvent =
  | { type: 'acquired'; key: LockKey; expiresAt: string | null; graceUntil: string | null }
  | { type: 'denied'; key: LockKey; reason: LockDeniedReason; status: number; holder: LockHolder | null; canTakeOver: boolean }
  | { type: 'released'; key: LockKey }
  | { type: 'lost'; key: LockKey; reason: LockLostReason; by: LockHolder | null }
  /** The socket closed: the server dropped every lock of it. */
  | { type: 'down'; key: LockKey }

/** The server's own block id rule (`utils/blockIds.js`), so a bad id never costs a frame. */
const BLOCK_ID = /^[A-Za-z0-9_-]{1,64}$/
const LANGUAGE = /^[a-z]{2}(-[A-Z]{2})?$/
/** Spacing between lock frames (the presence store keeps 300 ms on its own queue). */
export const LOCK_FRAME_GAP_MS = 300

export const isLockBlock = (block: unknown): block is string => typeof block === 'string' && BLOCK_ID.test(block)

/** The id of a lock key: `room|language|block` (`-` without a language). */
export const lockKeyId = (key: LockKey): string => `${key.room}|${key.language || '-'}|${key.block}`

const normLanguage = (language: unknown): string | null =>
  (typeof language === 'string' && LANGUAGE.test(language) ? language : null)

const asHolder = (raw: unknown): LockHolder | null => {
  const h = raw as Partial<LockHolder> | null
  if (!h || typeof h !== 'object' || typeof h.memberId !== 'string') return null
  return {
    memberId: h.memberId,
    name: typeof h.name === 'string' ? h.name : null,
    avatarUrl: typeof h.avatarUrl === 'string' ? h.avatarUrl : null,
  }
}

const keyOf = (event: Record<string, unknown>): LockKey | null => {
  if (typeof event.room !== 'string' || !isLockBlock(event.block)) return null
  return { room: event.room, block: event.block, language: normLanguage(event.language) }
}

/**
 * Whether a room lock covers a piece: the same block, and the same language
 * or one of the two without a language (the server's own overlap rule).
 */
export const lockCovers = (lock: Pick<RoomLock, 'block' | 'language'>, block: string, language: string | null | undefined): boolean =>
  lock.block === block && (!lock.language || !language || lock.language === language)

/** The `locks` list of a frame, or null when it is not one. */
export const parseLocksFrame = (event: Record<string, unknown>): { room: string; locks: RoomLock[] } | null => {
  if (event.type !== 'locks' || typeof event.room !== 'string') return null
  const locks = (Array.isArray(event.locks) ? event.locks : [])
    .map((raw): RoomLock | null => {
      const l = raw as Record<string, unknown> | null
      if (!l || !isLockBlock(l.block)) return null
      return {
        block: l.block,
        language: normLanguage(l.language),
        mine: l.mine === true,
        holder: asHolder(l.holder),
        expiresAt: typeof l.expiresAt === 'string' ? l.expiresAt : null,
      }
    })
    .filter((l): l is RoomLock => Boolean(l))
  return { room: event.room, locks }
}

const DENIED = new Set<LockDeniedReason>(['held', 'forbidden', 'notEditing', 'invalid', 'tooMany'])
const LOST = new Set<LockLostReason>(['takeover', 'idle', 'moved', 'access'])

interface Wanted {
  key: LockKey
  takeover: boolean
}

type Timers = {
  setTimeout: (fn: () => void, ms: number) => unknown
  clearTimeout: (id: unknown) => void
}

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (id) => globalThis.clearTimeout(id as ReturnType<typeof setTimeout>),
}

export const createEditLockStore = (
  send: (frame: Record<string, unknown>) => boolean,
  { gapMs = LOCK_FRAME_GAP_MS, timers = defaultTimers, now = () => Date.now() }: { gapMs?: number; timers?: Timers; now?: () => number } = {},
) => {
  /** Rooms this socket edits since the last `ready` (the server would refuse a lock elsewhere). */
  const editable = new Set<string>()
  /** room -> its live locks (from the last `locks` frame). */
  const roomLocks = new Map<string, RoomLock[]>()
  const roomListeners = new Map<string, Set<() => void>>()
  const keyListeners = new Map<string, Set<(event: LockEvent) => void>>()
  /** Locks the editors want (held or asked for), by key id. */
  const wanted = new Map<string, Wanted>()
  /** Keys whose acquire went to the server and were not released, lost or denied since: the server may hold them. */
  const sent = new Set<string>()
  const queue: { id: string; frame: Record<string, unknown> }[] = []
  let ready = false
  let pumpTimer: unknown = null
  let lastFrameAt = 0

  const notifyRoom = (room: string) => {
    for (const listener of [...(roomListeners.get(room) || [])]) listener()
  }
  const emit = (event: LockEvent) => {
    for (const listener of [...(keyListeners.get(lockKeyId(event.key)) || [])]) listener(event)
  }

  const pump = () => {
    if (pumpTimer !== null) return
    const step = () => {
      pumpTimer = null
      if (!queue.length) return
      const wait = lastFrameAt + gapMs - now()
      if (wait > 0) {
        pumpTimer = timers.setTimeout(step, wait)
        return
      }
      const { frame } = queue.shift()!
      lastFrameAt = now()
      send(frame)
      if (queue.length) pumpTimer = timers.setTimeout(step, gapMs)
    }
    step()
  }

  const frameOf = (type: 'lock.acquire' | 'lock.release', key: LockKey, takeover = false): Record<string, unknown> => ({
    type,
    room: key.room,
    block: key.block,
    ...(key.language ? { language: key.language } : {}),
    ...(type === 'lock.acquire' && takeover ? { takeover: true } : {}),
  })

  const sendAcquire = (entry: Wanted) => {
    const id = lockKeyId(entry.key)
    /* A newer acquire of the same key replaces one still waiting. */
    for (let i = queue.length - 1; i >= 0; i -= 1) if (queue[i].id === id) queue.splice(i, 1)
    sent.add(id)
    queue.push({ id, frame: frameOf('lock.acquire', entry.key, entry.takeover) })
    pump()
  }

  const sendRelease = (key: LockKey) => {
    const id = lockKeyId(key)
    const waiting = queue.findIndex((item) => item.id === id && item.frame.type === 'lock.acquire')
    for (let i = queue.length - 1; i >= 0; i -= 1) if (queue[i].id === id) queue.splice(i, 1)
    /* The acquire never left: the server knows nothing, nothing to release. */
    if (waiting >= 0 && !queue.some((item) => item.id === id)) {
      sent.delete(id)
      return
    }
    if (!sent.has(id)) return
    sent.delete(id)
    queue.push({ id, frame: frameOf('lock.release', key) })
    pump()
  }

  /** The room became editable: what the editors want there goes out now. */
  const flushRoom = (room: string) => {
    for (const entry of wanted.values()) {
      if (entry.key.room !== room) continue
      if (sent.has(lockKeyId(entry.key))) continue
      sendAcquire(entry)
    }
  }

  return {
    /**
     * Asks for a lock (again, after a denial or a loss). Waits for the room's
     * editing join; `takeover` (owner/admin) takes it from someone else.
     */
    acquire(key: LockKey, { takeover = false }: { takeover?: boolean } = {}) {
      if (!isLockBlock(key.block) || !key.room) return
      const entry: Wanted = { key: { ...key, language: normLanguage(key.language) }, takeover }
      const id = lockKeyId(entry.key)
      wanted.set(id, entry)
      if (ready && editable.has(entry.key.room)) {
        /* Already sent and still waiting for the answer: one frame is enough (a takeover always goes). */
        if (sent.has(id) && !takeover && queue.some((item) => item.id === id)) return
        sendAcquire(entry)
      }
    },
    /** Gives the lock back (or forgets the request). */
    release(key: LockKey) {
      const k = { ...key, language: normLanguage(key.language) }
      wanted.delete(lockKeyId(k))
      if (ready) sendRelease(k)
      else sent.delete(lockKeyId(k))
    },
    /** Whether this socket asked for the key and was not denied, released or dropped since. */
    isWanted(key: LockKey): boolean {
      return wanted.has(lockKeyId({ ...key, language: normLanguage(key.language) }))
    },
    /** A socket frame; true when it was a lock frame (or the editing join it waits for). */
    onFrame(event: Record<string, unknown>): boolean {
      switch (event.type) {
        case 'presence.joined': {
          if (typeof event.room !== 'string') return false
          if (event.editing === true) {
            const fresh = !editable.has(event.room)
            editable.add(event.room)
            if (fresh && ready) flushRoom(event.room)
          } else {
            editable.delete(event.room)
          }
          return false
        }
        case 'presence.denied':
        case 'presence.left': {
          if (typeof event.room === 'string') editable.delete(event.room)
          return false
        }
        case 'locks': {
          const parsed = parseLocksFrame(event)
          if (!parsed) return true
          roomLocks.set(parsed.room, parsed.locks)
          /* Only an editor gets this frame: the room is editable. */
          if (!editable.has(parsed.room)) {
            editable.add(parsed.room)
            if (ready) flushRoom(parsed.room)
          }
          notifyRoom(parsed.room)
          return true
        }
        case 'lock.acquired': {
          const key = keyOf(event)
          if (!key) return true
          sent.add(lockKeyId(key))
          const entry = wanted.get(lockKeyId(key))
          if (entry) entry.takeover = false
          emit({ type: 'acquired', key, expiresAt: typeof event.expiresAt === 'string' ? event.expiresAt : null, graceUntil: typeof event.graceUntil === 'string' ? event.graceUntil : null })
          return true
        }
        case 'lock.denied': {
          const key = keyOf(event)
          if (!key) return true
          const id = lockKeyId(key)
          sent.delete(id)
          const reason = DENIED.has(event.reason as LockDeniedReason) ? (event.reason as LockDeniedReason) : 'invalid'
          /* notEditing: the join may not have landed yet; it goes again with the next `presence.joined`. */
          if (reason === 'notEditing') editable.delete(key.room)
          else wanted.delete(id)
          emit({ type: 'denied', key, reason, status: typeof event.status === 'number' ? event.status : 0, holder: asHolder(event.holder), canTakeOver: event.canTakeOver === true })
          return true
        }
        case 'lock.released': {
          const key = keyOf(event)
          if (key) emit({ type: 'released', key })
          return true
        }
        case 'lock.lost': {
          const key = keyOf(event)
          if (!key) return true
          const id = lockKeyId(key)
          sent.delete(id)
          wanted.delete(id)
          const reason = LOST.has(event.reason as LockLostReason) ? (event.reason as LockLostReason) : 'idle'
          emit({ type: 'lost', key, reason, by: asHolder(event.by) })
          return true
        }
        default:
          return false
      }
    },
    /** `ready` arrived: the server knows no lock and no editing join of this socket. */
    onReady() {
      ready = true
      editable.clear()
      sent.clear()
      queue.length = 0
    },
    /** The socket closed: every lock it held is gone. */
    onDown() {
      ready = false
      editable.clear()
      queue.length = 0
      const held = [...wanted.values()]
      sent.clear()
      for (const room of [...roomLocks.keys()]) {
        roomLocks.delete(room)
        notifyRoom(room)
      }
      for (const entry of held) emit({ type: 'down', key: entry.key })
    },
    locksOf(room: string): RoomLock[] | null {
      return roomLocks.get(room) || null
    },
    subscribeRoom(room: string, listener: () => void): () => void {
      if (!roomListeners.has(room)) roomListeners.set(room, new Set())
      roomListeners.get(room)!.add(listener)
      return () => {
        const set = roomListeners.get(room)
        if (!set) return
        set.delete(listener)
        if (!set.size) roomListeners.delete(room)
      }
    },
    onKey(key: LockKey, listener: (event: LockEvent) => void): () => void {
      const id = lockKeyId({ ...key, language: normLanguage(key.language) })
      if (!keyListeners.has(id)) keyListeners.set(id, new Set())
      keyListeners.get(id)!.add(listener)
      return () => {
        const set = keyListeners.get(id)
        if (!set) return
        set.delete(listener)
        if (!set.size) keyListeners.delete(id)
      }
    },
    dispose() {
      if (pumpTimer !== null) timers.clearTimeout(pumpTimer)
      pumpTimer = null
      queue.length = 0
    },
  }
}

export type EditLockStore = ReturnType<typeof createEditLockStore>
